import * as React from "react";

import { useRegenerateSupplierFolderContractSummaryMutation } from "@/api/contract-family-pipeline";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ContractSummarySettingsNotice } from "@/views/contract-families/components/ContractSummarySettingsNotice";
import { UseBatchApiField } from "@/views/contract-families/components/UseBatchApiField";
import { useBatchApiChoice } from "@/views/contract-families/components/useBatchApiChoice";
import { useContractSummaryReadiness } from "@/views/contract-families/components/useContractSummaryReadiness";
import { getPipelineErrorMessage } from "@/views/contract-families/pipeline/pipelineErrors";
import {
  CONTRACT_SUMMARY_ACTION_LABELS,
  type ContractSummaryAction,
} from "@/views/suppliers/components/contract-summary-status";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierContractFolderId: string;
  /** "create" for a family without a published summary, "update" once one exists. */
  action?: ContractSummaryAction;
}

const DESCRIPTIONS: Record<ContractSummaryAction, string> = {
  create: "This will generate the contract summary for this family and publish it to the Master Collection.",
  update:
    "This will regenerate the contract summary for this family and publish a new version to the Master Collection.",
};

const PENDING_LABELS: Record<ContractSummaryAction, string> = { create: "Creating…", update: "Updating…" };

/** Confirms the one-click (re)generation: the automated run publishes the family version itself. */
export const ContractSummaryRegenerateDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  supplierContractFolderId,
  action = "create",
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const [regenerate, { isLoading }] = useRegenerateSupplierFolderContractSummaryMutation();
  const { useBatchApi, setUseBatchApi, executionMode } = useBatchApiChoice(open);
  const { isChecking, missing } = useContractSummaryReadiness(open, { requireBaseCollection: true });
  const isConfigured = missing.length === 0;

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const submit = async () => {
    setError(null);
    try {
      await regenerate({
        supplier_contract_folder_id: supplierContractFolderId,
        ...(executionMode ? { execution_mode: executionMode } : {}),
      }).unwrap();
      onOpenChange(false);
    } catch (requestError) {
      setError(getPipelineErrorMessage(requestError));
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => (next || !isLoading ? onOpenChange(next) : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{CONTRACT_SUMMARY_ACTION_LABELS[action]}</DialogTitle>
          <DialogDescription>{DESCRIPTIONS[action]}</DialogDescription>
        </DialogHeader>
        {isConfigured ? (
          <UseBatchApiField
            id="contract-summary-use-batch-api"
            checked={useBatchApi}
            onCheckedChange={setUseBatchApi}
            disabled={isLoading}
          />
        ) : (
          <ContractSummarySettingsNotice missing={missing} />
        )}
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={isLoading || isChecking || !isConfigured}>
            {isLoading ? PENDING_LABELS[action] : CONTRACT_SUMMARY_ACTION_LABELS[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
